import { useCallback, useEffect, useRef, useState } from 'react';
import { ChatRoom, SendMessageRequest } from 'amazon-ivs-chat-messaging';

import { CHAT_REGION } from '../../config';
import { fetchModeratorHistory, fetchModeratorToken, isUnauthorized } from '../../api';
import { EMPTY_DRAWS, buyersFromEvent, jackpotFromEvent, withDraw } from '../../draws';
import { purchaseFromEvent, upsertItem } from '../../purchases';
import { EMPTY_SCOREBOARD, scoreboardFromEvent, withScoreboard } from '../../scoreboard';
import { EMPTY_DISPLAY, displayFromEvent, withDisplay } from '../../display';
import { EMPTY_STOPWATCH, stopwatchFromEvent, withStopwatch } from '../../stopwatch';

const MAX_ITEMS = 1000; // like the phones: the history brings the last 200, the rest arrives live
const RECONNECT_MS = 3000; // after the room drops the panel: its session ran out, or the network did
// What the room sends reaches the list in batches this far apart, like on the phones (Chat.jsx):
// with everyone writing at once, a render per message leaves the panel no time for anything else
export const FLUSH_MS = 120;

const toItem = (message) => ({
  type: 'MESSAGE',
  id: message.id,
  userId: message.sender.userId,
  nickname: message.sender.attributes?.nickname || 'Alguien',
  content: message.content,
  sendTime: message.sendTime,
});

// An approved message reaches the room as an event, not as a message
const fromEvent = ({ attributes, sendTime }) => ({
  type: 'MESSAGE',
  id: attributes.id,
  userId: attributes.userId,
  nickname: attributes.nickname || 'Alguien',
  content: attributes.content,
  sendTime: attributes.sendTime || sendTime,
});

// The history and what arrived live, without repeats and in order
const merge = (a, b) => {
  const byId = new Map();
  [...a, ...b].forEach((item) => byId.set(item.id, item));
  return [...byId.values()]
    .sort((x, y) => new Date(x.sendTime) - new Date(y.sendTime))
    .slice(-MAX_ITEMS);
};

/**
 * Connects the panel to the room as a moderator: it sees what every phone sees (published
 * messages and purchases, both jackpots and the scoreboard) and can announce something.
 * What was published before it opened comes from the history, like on the phones.
 */
const useModeratorRoom = (adminKey, onUnauthorized) => {
  const [messages, setMessages] = useState([]);
  const [status, setStatus] = useState('connecting');
  const [draws, setDraws] = useState(EMPTY_DRAWS);
  const [scoreboard, setScoreboard] = useState(EMPTY_SCOREBOARD);
  const [display, setDisplay] = useState(EMPTY_DISPLAY);
  const [stopwatch, setStopwatch] = useState(EMPTY_STOPWATCH);
  const roomRef = useRef(null);
  const reconnectTimer = useRef(null);
  const updates = useRef([]);
  const flushTimer = useRef(null);

  useEffect(() => {
    if (!adminKey) return undefined;

    // Everything the room changes in the list, in the order it arrived and in one render per batch
    const updateMessages = (update) => {
      updates.current.push(update);
      if (flushTimer.current) return;
      flushTimer.current = setTimeout(() => {
        flushTimer.current = null;
        const batch = updates.current;
        updates.current = [];
        setMessages((prev) => batch.reduce((items, apply) => apply(items), prev));
      }, FLUSH_MS);
    };

    const room = new ChatRoom({
      regionOrUrl: CHAT_REGION,
      tokenProvider: async () => {
        try {
          const data = await fetchModeratorToken(adminKey);
          return {
            token: data.token,
            sessionExpirationTime: new Date(data.sessionExpirationTime),
            tokenExpirationTime: new Date(data.tokenExpirationTime),
          };
        } catch (err) {
          if (isUnauthorized(err)) onUnauthorized();
          throw err;
        }
      },
    });
    room.logLevel = 'error'; // the SDK logs every event to the console otherwise: noise, and work, with a busy room
    roomRef.current = room;

    const add = (item) => updateMessages((prev) => (prev.some(({ id }) => id === item.id) ? prev : [...prev, item].slice(-MAX_ITEMS)));
    const drop = (id) => updateMessages((prev) => prev.filter((item) => item.id !== id));

    const unsubscribers = [
      room.addListener('connecting', () => setStatus('connecting')),
      room.addListener('connect', () => {
        setStatus('connected');
        fetchModeratorHistory(adminKey)
          .then((history) => updateMessages((live) => merge(history, live)))
          .catch((err) => {
            if (isUnauthorized(err)) onUnauthorized();
          });
      }),
      room.addListener('disconnect', () => {
        setStatus('disconnected');
        // The panel stays open for hours: whatever closed the connection, get back in
        clearTimeout(reconnectTimer.current);
        reconnectTimer.current = setTimeout(() => room.connect(), RECONNECT_MS);
      }),
      room.addListener('message', (message) => add(toItem(message))),
      room.addListener('event', (event) => {
        if (event.eventName === 'app:MESSAGE') add(fromEvent(event));
        if (event.eventName === 'app:PURCHASE') updateMessages((prev) => upsertItem(prev, purchaseFromEvent(event), MAX_ITEMS));
        if (event.eventName === 'app:DELETE') drop(event.attributes.id); // a purchase taken down
        if (event.eventName === 'app:CLEAR' && event.attributes.scope === 'chat') updateMessages(() => []); // a panel wiped the chat
        if (event.eventName === 'app:BUYERS') setDraws((current) => withDraw(current, buyersFromEvent(event)));
        if (event.eventName === 'app:JACKPOT') setDraws((current) => withDraw(current, jackpotFromEvent(event)));
        if (event.eventName === 'app:SCORE') setScoreboard((current) => withScoreboard(current, scoreboardFromEvent(event)));
        if (event.eventName === 'app:STOPWATCH') setStopwatch((current) => withStopwatch(current, stopwatchFromEvent(event)));
        if (event.eventName === 'app:DISPLAY') setDisplay((current) => withDisplay(current, displayFromEvent(event))); // another panel hid or showed something
      }),
      room.addListener('messageDelete', ({ messageId }) => drop(messageId)),
    ];

    room.connect();
    return () => {
      unsubscribers.forEach((unsubscribe) => unsubscribe());
      clearTimeout(reconnectTimer.current);
      clearTimeout(flushTimer.current);
      flushTimer.current = null;
      updates.current = [];
      room.disconnect();
    };
  }, [adminKey, onUnauthorized]);

  const dropMessage = (id) => setMessages((prev) => prev.filter((item) => item.id !== id));
  // The room resends the purchase without the photo too, but the panel need not wait for it
  const dropPhoto = (id) => setMessages((prev) => prev.map((item) => (item.id === id ? { ...item, photo: undefined } : item)));

  // The whole chat went: the room says so too, but the panel that did it need not wait either
  const clearMessages = () => setMessages([]);

  const announce = (content) => roomRef.current.sendMessage(new SendMessageRequest(content));

  // The scoreboard as the API gave it (the panel state, or the answer to a change): the room
  // says the same a moment later, and whichever comes second changes nothing
  const syncScoreboard = useCallback((incoming) => setScoreboard((current) => withScoreboard(current, incoming)), []);

  // What the phones show, the same way: from the API, and from the room if another panel changes it
  const syncDisplay = useCallback((incoming) => setDisplay((current) => withDisplay(current, incoming)), []);

  const syncStopwatch = useCallback((incoming) => setStopwatch((current) => withStopwatch(current, incoming)), []);

  return { messages, status, draws, scoreboard, display, stopwatch, dropMessage, dropPhoto, clearMessages, announce, syncScoreboard, syncDisplay, syncStopwatch };
};

export default useModeratorRoom;
